import { useEffect, useState } from 'react'

import classNames from 'classnames'

import MilesTracker from '@src/components/MilesTracker'
import ReferralCodeInput from '@src/components/ReferralCodeInput'
import { SwapButton } from '@src/components/buttons/SwapButton'
import ChevronLeftIcon from '@src/components/icons/ChevronLeft'
import InfoIcon from '@src/components/icons/Info'
import { ChainId, NATIVE_TOKEN_ADDRESS, WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { ResultCode, StepNames } from '@src/enums'
import { LiquiditySource } from '@src/enums'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import TransactionDetails from '@src/pages/Exchange/components/SwapInProgressFlow/TransactionDetails'
import useExchangeBalances from '@src/pages/Exchange/hooks/useExchangeBalances'
import { useSwapContract } from '@src/pages/Exchange/hooks/useSwapContract'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { Balance, isFeatureEnabled } from '@src/utils'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { getAllowance } from '@src/utils/getAllowance'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const transactionLogger = createContextLogger('TransactionSummary')

interface ITransactionSummaryProps {
    onClose: () => void
    onConfirm: () => Promise<void>
    currentStep: StepNames
    sellData: IExchangeData
    buyData: IExchangeData
    fees: IFee[]
    destinationAddress?: string
    miles?: number
    isOpen: boolean
    liquiditySource: LiquiditySource
    isCCTPTransfer: boolean
}

export default function TransactionSummary({
    onClose,
    onConfirm,
    currentStep,
    sellData,
    buyData,
    fees,
    destinationAddress,
    miles,
    isOpen,
    liquiditySource,
    isCCTPTransfer,
}: ITransactionSummaryProps) {
    // Zustand store variables
    const { exchangeId, setWaitingForConnectedWalletInteraction } = useApplicationStore()
    const updateHistory = useHistoryStore(state => state.updateHistory)

    // Wallet variable
    const { tronWalletAddress, solanaWalletAddress, evmWalletAddress } = useWalletConnections()

    const [isApprovalRequired, setIsApprovalRequired] = useState(false)
    const swapContract = useSwapContract(sellData, liquiditySource || LiquiditySource.unavailable)

    const { nativeBalance, wrappedNativeBalance, refreshAllBalancesAndWait } = useExchangeBalances()

    const tokenBalance = new Balance(sellData.token).setValue(sellData.token.userBalance ?? BigInt(0))

    // Check balances, approvals, and determine initial step
    useEffect(() => {
        const refreshBalances = async () => {
            await refreshAllBalancesAndWait()
        }

        if (!isOpen) {
            refreshBalances()
            return
        }

        const determineStep = async () => {
            transactionLogger.info('Determining step...')
            // Don't change anything if we're in a pending state
            if (isPendingStep(currentStep)) {
                return
            }

            if (!evmWalletAddress || !sellData.token?.address || !sellData.value) {
                setIsApprovalRequired(false)
                return
            }

            // Add check for Solana CCTP_DIRECT
            if (sellData.chainId === ChainId.Solana && liquiditySource === LiquiditySource.cctpDirect) {
                setIsApprovalRequired(false)

                updateHistory(exchangeId, {
                    currentStep: StepNames.CONFIRM_SWAP,
                })

                return
            }

            if (isWrapOnly(sellData, buyData)) {
                // Wrap only

                updateHistory(exchangeId, {
                    currentStep: StepNames.DEPOSIT_WETH,
                    result: {
                        status: ResultCode.pending,
                        message: `Wrapping ${sellData.token.symbol}`,
                    },
                })

                return
            } else if (isUnWrapOnly(sellData, buyData)) {
                // Unwrap only
            } else {
                // Every other order book swap

                const isNativeToken = sellData.token.address === NATIVE_TOKEN_ADDRESS[sellData.chainId]
                const wrappedToken = WRAPPED_GAS_TOKENS[sellData.chainId]
                const sellAmount = sellData.value

                try {
                    // For native tokens (e.g., POL)
                    if (isNativeToken && wrappedToken && sellData.chainId !== ChainId.Celo) {
                        const wrappedTokenBalance = wrappedNativeBalance?.bigintValue || BigInt(0)
                        const nativeTokenBalance = nativeBalance?.bigintValue || BigInt(0)
                        const sellAmountBigInt = BigInt(sellAmount)

                        // First check if we have enough native balance
                        if (nativeTokenBalance < sellAmountBigInt) {
                            updateHistory(exchangeId, {
                                currentStep: StepNames.UNSTARTED,
                                result: {
                                    status: ResultCode.failure,
                                    message: `Insufficient ${sellData.token.symbol} balance`,
                                },
                            })

                            return
                        }

                        // Check if we have enough wrapped balance
                        if (wrappedTokenBalance >= sellAmountBigInt) {
                            // Check if we need approval for the wrapped token
                            const allowance = await getAllowance(
                                +sellData.chainId,
                                wrappedToken.address,
                                evmWalletAddress,
                                swapContract.address
                            )

                            const needsApproval = BigInt(allowance) < sellAmountBigInt
                            setIsApprovalRequired(needsApproval)

                            // Set appropriate step

                            updateHistory(exchangeId, {
                                currentStep: needsApproval ? StepNames.APPROVE_TOKENS : StepNames.CONFIRM_SWAP,
                            })

                            return
                        }

                        // If we don't have enough wrapped balance, we need to wrap first
                        setIsApprovalRequired(true) // We'll need approval after wrapping

                        updateHistory(exchangeId, {
                            currentStep: StepNames.DEPOSIT_WETH,
                            result: {
                                status: ResultCode.pending,
                                message: `Wrapping ${sellData.token.symbol}`,
                            },
                        })

                        return
                    }

                    // For non-native tokens
                    const tokenBalanceBigInt = tokenBalance?.bigintValue || BigInt(0)
                    const sellAmountBigInt = BigInt(sellAmount)

                    transactionLogger.info('Token balance and sell amount', {
                        tokenBalanceBigInt: tokenBalanceBigInt.toString(),
                        sellAmountBigInt: sellAmountBigInt.toString(),
                    })

                    // Check token balance first
                    if (tokenBalanceBigInt < sellAmountBigInt) {
                        updateHistory(exchangeId, {
                            currentStep: StepNames.UNSTARTED,
                            result: {
                                status: ResultCode.failure,
                                message: `Insufficient ${sellData.token.symbol} balance`,
                            },
                        })

                        return
                    }

                    transactionLogger.info('Chain ID', { chainId: sellData.chainId })
                    const walletAddress =
                        +sellData.chainId === ChainId.Tron
                            ? tronWalletAddress
                            : +sellData.chainId === ChainId.Solana
                              ? solanaWalletAddress
                              : evmWalletAddress

                    // Then check allowance
                    const allowance = await getAllowance(
                        +sellData.chainId,
                        sellData.token.address,
                        walletAddress,
                        swapContract.address
                    )

                    transactionLogger.info('Allowance details', {
                        allowance,
                        sellValue: sellData.value,
                        chainType:
                            sellData.chainId === ChainId.Solana
                                ? 'solana'
                                : sellData.chainId === ChainId.Tron
                                  ? 'tron'
                                  : 'evm',
                    })

                    const needsApproval = BigInt(allowance) < BigInt(sellData.value)
                    setIsApprovalRequired(needsApproval)

                    updateHistory(exchangeId, {
                        currentStep: needsApproval ? StepNames.APPROVE_TOKENS : StepNames.CONFIRM_SWAP,
                    })
                } catch (error) {
                    transactionLogger.error('Error checking balances and allowances:', {
                        error,
                    })
                    setIsApprovalRequired(false)

                    updateHistory(exchangeId, {
                        currentStep: StepNames.UNSTARTED,
                        result: {
                            status: ResultCode.failure,
                            message: 'Error checking balances and allowances. Please try again.',
                        },
                    })
                }
            }
        }

        determineStep()
    }, [
        evmWalletAddress,
        sellData.token?.address,
        sellData.value,
        sellData.chainId,
        swapContract.address,
        exchangeId,
        wrappedNativeBalance?.bigintValue,
        nativeBalance?.bigintValue,
        tokenBalance?.bigintValue,
    ])

    // Handle step progression
    const handleConfirm = async () => {
        try {
            setWaitingForConnectedWalletInteraction(true)

            // If we're at the final confirmation step
            if (currentStep === StepNames.CONFIRM_SWAP) {
                updateHistory(exchangeId, {
                    currentStep: StepNames.CONFIRM_SWAP_PENDING,
                })
            } else if (currentStep === StepNames.DEPOSIT_WETH) {
                updateHistory(exchangeId, {
                    currentStep: StepNames.DEPOSIT_WETH_PENDING,
                })
            } else if (currentStep === StepNames.APPROVE_TOKENS) {
                updateHistory(exchangeId, {
                    currentStep: StepNames.APPROVE_TOKENS_PENDING,
                })
            }

            // Call onConfirm which handles the actual transaction
            await onConfirm()
        } catch (error: any) {
            transactionLogger.error('Transaction error:', { error })

            // Check for user rejection
            const isUserRejection =
                error?.message?.includes('rejected') ||
                error?.message?.includes('user denied') ||
                error?.message?.includes('user cancelled')

            if (isUserRejection) {
                // Keep the modal open and reset to the appropriate step

                updateHistory(exchangeId, {
                    currentStep:
                        currentStep === StepNames.DEPOSIT_WETH_PENDING
                            ? StepNames.DEPOSIT_WETH
                            : currentStep === StepNames.APPROVE_TOKENS_PENDING
                              ? StepNames.APPROVE_TOKENS
                              : currentStep === StepNames.CONFIRM_SWAP_PENDING
                                ? StepNames.CONFIRM_SWAP
                                : currentStep,
                    result: {
                        status: ResultCode.pending,
                        message: 'Transaction was rejected. Please try again.',
                    },
                })

                setWaitingForConnectedWalletInteraction(false)
            } else {
                // For other errors, show error message but keep modal open

                updateHistory(exchangeId, {
                    exchangeId,
                    currentStep:
                        currentStep === StepNames.DEPOSIT_WETH_PENDING
                            ? StepNames.DEPOSIT_WETH
                            : currentStep === StepNames.APPROVE_TOKENS_PENDING
                              ? StepNames.APPROVE_TOKENS
                              : currentStep === StepNames.CONFIRM_SWAP_PENDING
                                ? StepNames.CONFIRM_SWAP
                                : currentStep,
                    result: {
                        status: ResultCode.failure,
                        message: 'Transaction failed. Please try again.',
                    },
                })

                setWaitingForConnectedWalletInteraction(false)
            }
        }
    }

    // Add this function near the top of the component
    const isPendingStep = (step: StepNames) => {
        return [
            StepNames.DEPOSIT_WETH_PENDING,
            StepNames.APPROVE_TOKENS_PENDING,
            StepNames.CONFIRM_SWAP_PENDING,
            StepNames.MATCHING,
            StepNames.MATCH_CREATED,
        ].includes(step)
    }

    // Update the close handler
    const handleClose = () => {
        const pendingStates = [
            StepNames.DEPOSIT_WETH_PENDING,
            StepNames.APPROVE_TOKENS_PENDING,
            StepNames.CONFIRM_SWAP_PENDING,
            StepNames.MATCHING,
            StepNames.MATCH_CREATED,
        ]

        // Prevent closing if we're in a pending state
        if (pendingStates.includes(currentStep)) {
            return
        }

        // Reset waiting state when modal is closed
        setWaitingForConnectedWalletInteraction(false)
        onClose()
    }

    const handleMetaMaskPopup = (step: StepNames) => {
        updateHistory(exchangeId, {
            currentStep: step,
        })

        setWaitingForConnectedWalletInteraction(true)
    }

    return (
        <>
            <div className='flex w-full items-center px-4'>
                <div className='flex-1'>
                    <ChevronLeftIcon
                        width={24}
                        height={24}
                        onClick={handleClose}
                        className={classNames('cursor-pointer', {
                            'cursor-not-allowed opacity-50': isPendingStep(currentStep),
                        })}
                    />
                </div>
                <p className='flex-10 text-2xl font-bold'>Transaction Summary</p>
                <div className='flex-1'></div>
            </div>
            <div className='space-y-4 px-4 text-sm text-mach-accent-forground md:space-y-6'>
                <TransactionDetails
                    sellData={sellData}
                    buyData={buyData}
                    fees={fees}
                    destinationAddress={destinationAddress}
                    currentStep={currentStep}
                    liquiditySource={liquiditySource}
                    isCCTPTransfer={isCCTPTransfer}
                />

                {!!miles && !isFeatureEnabled('USE_TESTNETS_ONLY') && <MilesTracker miles={miles} />}
                {!isFeatureEnabled('USE_TESTNETS_ONLY') && <ReferralCodeInput />}
            </div>
            <SwapButton
                errorsJSON={{
                    isInsufficientBalance: false,
                    isQuoteInvalid: false,
                    isBuyOutputLoading: false,
                }}
                sellData={sellData}
                buyData={buyData}
                className='mt-auto w-full'
                onClick={handleConfirm}
                currentStep={currentStep}
                onMetaMaskPopup={handleMetaMaskPopup}
            />
        </>
    )
}
